import type React from 'react';
import {generatePath, Link as RouterLink, useNavigate, useParams} from 'react-router';
import {Alert, Box, Button, Paper, Skeleton, Stack, Typography} from '@mui/material';
import {CheckCircle, RadioButtonUnchecked} from '@mui/icons-material';
import {MainPageFacade} from '~/components/main/page/MainPageFacade';
import {useCustomQuery} from '~/query/queryClient';
import {queryHttp} from '~/query/queryHttp';
import {routes} from '~/routing/routes';
import {useCategoryPlanQuery, usePlanActions} from '~/pages/category-profile/hooks/useCategoryPlan';
import {useMoneyFormat} from '~/pages/category-profile/hooks/useMoneyFormat';
import {MarkdownText} from '~/components/MarkdownEditor/MarkdownEditor';
import {GatewayCard} from '~/pages/category-profile/profile/strategy/rail/GatewayCard';
import {planApprovalUrl} from '~/pages/category-profile/services/categoryProfileUrls';

type PlanApprovalRequest = {
    id: number;
    status: 'pending' | 'approved' | 'rejected' | string;
    statusBy: string;
    statusAt: string;
    round: number;
    /** False once the plan was resubmitted: a later round has its own request. */
    current: boolean;
    databagId: number;
    databagName: string;
    path: string;
    submittedBy: string;
    submittedAt: string;
    note: string;
    feedback: string;
};

const WHEN = new Intl.DateTimeFormat('en-GB', {day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'});

const OUTCOME: Record<string, {severity: 'success' | 'warning' | 'info'; text: string}> = {
    approved: {severity: 'success', text: 'Approved'},
    rejected: {severity: 'warning', text: 'Closed without approval'},
};

/** A category plan on the Approval page: what was submitted, the quality checks, and the approvers' sign-off. */
export const ApprovalCategoryPlanPage: React.FC = () => {
    const {approvalId = ''} = useParams<{approvalId: string}>();
    const navigate = useNavigate();
    const money = useMoneyFormat();
    const request = useCustomQuery({
        queryKey: [planApprovalUrl(approvalId)] as const,
        queryFn: async () => (await queryHttp.get<PlanApprovalRequest>(planApprovalUrl(approvalId))).data,
        enabled: Boolean(approvalId),
        retry: false,
    });
    const r = request.data;
    const {data: plan, isError: planError} = useCategoryPlanQuery(r?.databagId ?? 0, r?.path ?? '');
    const actions = usePlanActions(r?.databagId ?? 0, r?.path ?? '');

    const category = r ? r.path.split('|').join(' › ') : '';
    const profileLink = r ? `${generatePath(routes.category_profile, {id: String(r.databagId), tab: 'strategy'})}?${
        new URLSearchParams({path: r.path})}` : '';
    const outcome = r && OUTCOME[r.status];

    return (
        <MainPageFacade title="Review Category Plan" onBack={() => navigate(routes.approval)}
                        actionBarStart={r && <Typography>{`Category Plan ― ${r.submittedBy} ― ${WHEN.format(new Date(r.submittedAt))}`}</Typography>}
                        className="approval-page">
            {request.isError && <Alert severity="error">Could not load this approval request.</Alert>}
            {request.isLoading && <Stack gap={2}><Skeleton height={48}/><Skeleton variant="rounded" height={360}/></Stack>}
            {r && (
                <Box sx={{display: 'grid', gap: 3, alignItems: 'start', gridTemplateColumns: {xs: '1fr', lg: 'minmax(0, 1fr) 360px'}}}>
                    <Stack gap={2} sx={{minWidth: 0}}>
                        <Box>
                            <Typography variant="h5" component="h1" sx={{fontWeight: 600}}>{category}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {`${r.databagName} · round ${r.round} · submitted by ${r.submittedBy}`}
                            </Typography>
                        </Box>
                        {!r.current && (
                            <Alert severity="info">The plan was submitted again since; this round is closed.</Alert>
                        )}
                        {outcome && (
                            <Alert severity={outcome.severity}>
                                {`${outcome.text} by ${r.statusBy}, ${WHEN.format(new Date(r.statusAt))}.`}
                                {r.feedback && <><br/>{r.feedback}</>}
                            </Alert>
                        )}
                        {r.note && (
                            <Paper variant="outlined" sx={{p: 2}}>
                                <Typography variant="subtitle2">Submission note</Typography>
                                <Typography variant="body2" sx={{whiteSpace: 'pre-wrap'}}>{r.note}</Typography>
                            </Paper>
                        )}
                        {planError && <Alert severity="error">Could not load the plan.</Alert>}
                        {!plan && !planError && <Skeleton variant="rounded" height={240}/>}
                        {plan && (
                            <>
                                <Paper variant="outlined" sx={{p: 2}}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="baseline" gap={2} flexWrap="wrap">
                                        <Typography variant="subtitle1" sx={{fontWeight: 600}}>{plan.content.meta.name}</Typography>
                                        <Button component={RouterLink} to={profileLink} size="small">Open the full plan</Button>
                                    </Stack>
                                    <Box sx={{mt: 1}}><MarkdownText markdown={plan.content.summary}/></Box>
                                    <Box sx={{display: 'grid', gap: 2, mt: 2, gridTemplateColumns: {xs: '1fr 1fr', md: 'repeat(4, 1fr)'}}}>
                                        {[
                                            ['Influenceable spend', money(plan.content.meta.influenceableSpend)],
                                            ...(plan.derived.toolsOff.includes('windmill') ? [] : [['Kraljic', plan.derived.kraljic.quadrant]]),
                                            ...(plan.derived.toolsOff.includes('pick') ? [] : [['Savings target', money(plan.derived.targets.financial)]]),
                                            ['Horizon', plan.content.meta.horizon],
                                        ].map(([label, value]) => (
                                            <div key={label}>
                                                <Typography variant="caption" color="text.secondary">{label}</Typography>
                                                <Typography variant="body1" sx={{fontWeight: 600}}>{value}</Typography>
                                            </div>
                                        ))}
                                    </Box>
                                    <Typography variant="caption" color="text.secondary" sx={{display: 'block', mt: 1}}>
                                        {`Spend basis: ${plan.period}.`}
                                    </Typography>
                                </Paper>
                                <Paper variant="outlined" sx={{p: 2}}>
                                    <Typography variant="subtitle1" sx={{fontWeight: 600}}>
                                        {`Quality checks: ${plan.derived.qualityMet} of ${plan.derived.quality.length} met`}
                                    </Typography>
                                    <Stack gap={1} sx={{mt: 1}}>
                                        {plan.derived.quality.map(q => (
                                            <Stack key={q.key} direction="row" gap={1} alignItems="flex-start">
                                                {q.met ? <CheckCircle fontSize="small" color="success"/>
                                                    : <RadioButtonUnchecked fontSize="small" color="disabled"/>}
                                                <div>
                                                    <Typography variant="body2">{q.label}</Typography>
                                                    <Typography variant="caption" color="text.secondary">{q.evidence}</Typography>
                                                </div>
                                            </Stack>
                                        ))}
                                    </Stack>
                                </Paper>
                            </>
                        )}
                    </Stack>
                    {plan && r.current && <GatewayCard plan={plan} actions={actions} onApprovalPage/>}
                    {plan && !r.current && plan.approvalRequestId && (
                        <Button variant="outlined" component={RouterLink}
                                to={generatePath(routes.approval_plan_detail, {approvalId: String(plan.approvalRequestId)})}>
                            Open the latest round
                        </Button>
                    )}
                </Box>
            )}
        </MainPageFacade>
    );
};
